// src/PrivateRoute.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import api from "./constants/api.js";
import { canSeeMgmt } from "./constants/roles.js";
import { useTheme } from "./context/ThemeContext.jsx";
import { useAuth } from "./components/ui/AuthContext.jsx";
import { Zap } from "lucide-react";
import { ForbiddenPage } from "./pages/ErrorPage.jsx";

/* Korrektes Rollen-Mapping aus der DB */
const ROLE_MAP = {
  1: "admin",
  2: "gl",
  3: "pl",
  4: "sek",
  5: "mitarbeiter",
  6: "lehrling"
};
const norm = v => String(v ?? "").trim().toLowerCase().replaceAll("ä", "ae").replaceAll("ö", "oe").replaceAll("ü", "ue");
function roleKeyFromUser(u) {
  const rid = Number(u?.rolle_id ?? u?.role_id ?? NaN);
  if (!Number.isNaN(rid) && ROLE_MAP[rid]) return ROLE_MAP[rid];
  const rs = norm(u?.rolle ?? u?.role ?? u?.rolle_key ?? u?.role_key);
  if (!rs) return "";
  if (/(sek|buero|büro|sekretariat)/.test(rs)) return "sek";
  if (rs.startsWith("geschaeftsleit") || rs.startsWith("geschaftsleit") || rs.startsWith("geschäftsleit")) return "gl";
  if (rs.startsWith("projektleit")) return "pl";
  return rs;
}
function isAllowedByProp(user, allowed) {
  if (!allowed || Array.isArray(allowed) && allowed.length === 0) return true;
  const a = Array.isArray(allowed) ? allowed : [allowed];
  const allowedSet = new Set(a.map(x => norm(x)));
  if (allowedSet.has("mgmt") || allowedSet.has("management")) {
    return canSeeMgmt(user);
  }
  const rk = roleKeyFromUser(user);
  return allowedSet.has(rk);
}
export default function PrivateRoute({
  children,
  allowed,
}) {
  const { user, loadingAuth, refreshAuth } = useAuth();
  const loc = useLocation();
  const allowedMemo = useMemo(() => allowed, [allowed]);
  const [authRedirect, setAuthRedirect] = useState(null);

  // Einmalige Prüfung für 2FA / Passwort-Wechsel (nicht bei jedem Route-Wechsel)
  useEffect(() => {
    if (loadingAuth || user) {
      setAuthRedirect(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/check-login");
        if (!alive) return;
        if (data?.user) {
          if (!user) await refreshAuth(true);
          return;
        }
        const path = loc.pathname + loc.search;
        if (data?.need2FA && !path.startsWith("/2fa")) {
          setAuthRedirect("/2fa/setup");
          return;
        }
        if (data?.needChangePassword && path !== "/first-password") {
          setAuthRedirect("/first-password");
          return;
        }
        // needAcademy: kein Hard-Redirect — App bleibt nutzbar (Visit-Tracking)
        try {
          sessionStorage.setItem("intended", path);
        } catch {}
        setAuthRedirect("/login");
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        const flags = err?.response?.data || {};
        const path = loc.pathname + loc.search;
        if (status === 401 || status === 403) {
          try {
            sessionStorage.setItem("intended", path);
          } catch {}
        }
        if (status === 403 && flags.need2FA && !loc.pathname.startsWith("/2fa")) {
          setAuthRedirect("/2fa/setup");
          return;
        }
        if (status === 403 && flags.needChangePassword && loc.pathname !== "/first-password") {
          setAuthRedirect("/first-password");
          return;
        }
        setAuthRedirect("/login");
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadingAuth, user, loc.pathname, loc.search, refreshAuth]);

  if (loadingAuth && !user) {
    return <LoadingScreen isDark={true} />;
  }

  if (!user) {
    if (authRedirect && loc.pathname !== authRedirect) {
      return <Navigate to={authRedirect} replace />;
    }
    if (!authRedirect) {
      return <LoadingScreen isDark={true} />;
    }
    return null;
  }

  if (!isAllowedByProp(user, allowedMemo)) {
    return <ForbiddenPage />;
  }

  return children;
}

function LoadingScreen({
  isDark
}) {
  const {
    isDark: themeDark
  } = useTheme();
  const dark = isDark ?? themeDark;
  return <div className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 ${dark ? "bg-[#0a0f1a]" : "bg-slate-50"}`}>
      <div className="relative">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${dark ? "bg-amber-500/20" : "bg-amber-100"}`}>
          <Zap className={`w-8 h-8 ${dark ? "text-amber-400" : "text-amber-600"} animate-pulse`} />
        </div>
      </div>
      <p className={`text-sm ${dark ? "text-white/60" : "text-slate-500"}`}>Lade…</p>
    </div>;
}
